<template>
  <div
    class="w-full bg-zt-white rounded-zt-md p-6"
    :class="[boxLogin.styles.shadow]"
    :style="[boxLogin.styles.box]"
  >
    <div class="flex flex-col items-center">
      <div
        class="mb-4 flex justify-center h-full"
        :class="[boxLogin.sizeLogo === 'full' ? 'w-full' : 'w-56']"
      >
        <img class="format-img" :src="boxLogin.logoUrl" alt="" />
      </div>
      <div
        v-if="!isFirstLoginSuccess && !isFirstLoginEmptyEmail"
        class="text-center w-11/12"
        :style="[boxLogin.styles.form.textColor]"
      >
        <h1 class="text-zt-lg mb-3 font-bold">
          {{ $t('message.loginComponent.firstLogin.firstLoginText') }}
        </h1>
        <p>{{ $t('message.loginComponent.firstLogin.changePassSecurity') }}</p>
      </div>
      <div class="w-full" v-if="firstLoginErrors.showMessage">
        <zt-alert
          :text="firstLoginErrors.message"
          color="danger"
          :duration="firstLoginErrors.MAX_DURATION_ALERT"
          @afterClosed="firstLoginErrors.showMessage = false"
        />
      </div>
      <div class="mt-4 w-full">
        <ValidationObserver v-slot="{ invalid }">
          <zt-auth-first-login-fields
            v-if="!isFirstLoginSuccess && !isFirstLoginEmptyEmail"
            v-model="firstLoginFields"
            :first-login-custom-params-fields="boxLogin"
            :password-policies="passwordPolicies"
          />
          <zt-auth-first-login-success
            v-if="isFirstLoginSuccess"
            :first-login-custom-params-fields="boxLogin"
          />
          <zt-auth-first-login-email-required
            v-if="isFirstLoginEmptyEmail"
            :first-login-custom-params-fields="boxLogin"
            :user-password="firstLoginFields.password"
            @changeEmailSuccess="changeEmailSuccess"
          />
          <div
            class="mb-4"
            v-if="!isFirstLoginSuccess && !isFirstLoginEmptyEmail"
          >
            <zt-button
              class="zt-button-first-login w-full opacity-100"
              :style="[bgButtonColor(invalid)]"
              :disabled="invalid || disabledButton"
              @click="submitNextStep"
              data-vs-button="auth-firstlogin-btn-nextstep"
            >
              <span v-if="!isLoadingFirstLogin">
                {{ $t('message.licenseComponent.licenseBox.next') }}
              </span>
              <vue-loading
                v-if="isLoadingFirstLogin"
                background-color="rgba(0,0,0,0.0)"
                :active="isLoadingFirstLogin"
                color="#4F41A3"
                :width="30"
                :height="30"
                :is-full-page="false"
              />
            </zt-button>
          </div>
        </ValidationObserver>
      </div>
    </div>

    <div
      class="flex flex-col items-center"
      v-if="!isFirstLoginEmptyEmail && !isFirstLoginSuccess"
    >
      <a
        @click="activeModalFirstLogin"
        class="text-zt-dark-300 flex items-center justify-center"
        :style="[boxLogin.styles.form.textColor]"
      >
        <p>{{ $t('message.loginComponent.login.help') }}</p>
      </a>
    </div>

    <zt-auth-first-login-help-dialog
      v-if="!isFirstLoginEmptyEmail"
      :isActiveModalFirstLogin="isActiveModalFirstLogin"
    />
    <zt-auth-email-required-help-dialog
      v-else
      :isActiveModalFirstLogin="isActiveModalFirstLogin"
    />
  </div>
</template>

<script>
import loginCustomParams from '@/modules/Auth/mixins/loginCustomParams'
import ZtAuthFirstLoginFields from '@/modules/Auth/components/authFirstLoginFields'
import { mapGetters, mapActions } from 'vuex'
import { isEmpty } from 'lodash'
import Users from '@/apiClient/Users'
import ZtAuthFirstLoginSuccess from '@/modules/Auth/components/authFirstLoginSuccess'
import ZtAuthFirstLoginEmailRequired from '@/modules/Auth/components/authFirstLoginEmailRequired'
import ztAuthFirstLoginHelpDialog from '@/modules/Auth/components/authFirstLoginHelpDialog'
import ztAuthEmailRequiredHelpDialog from '@/modules/Auth/components/authEmailRequiredHelpDialog'
import Company from '@/apiClient/Company'
import { store, mutation } from '@/modules/Auth/hooks/activeFirstLoginModal'

export default {
  name: 'ztFirstLogin',
  components: {
    ZtAuthFirstLoginEmailRequired,
    ZtAuthFirstLoginSuccess,
    ZtAuthFirstLoginFields,
    ztAuthFirstLoginHelpDialog,
    ztAuthEmailRequiredHelpDialog
  },
  provide: { store, mutation },
  data() {
    return {
      objectLoginParams: {},
      isLoadingFirstLogin: false,
      firstLoginFields: {
        email: '',
        password: '',
        rePassword: '',
        activeTermsConditions: false
      },
      disabledButton: false,
      firstLoginErrors: {
        message: '',
        showMessage: false,
        MAX_DURATION_ALERT: 4
      },
      isFirstLoginSuccess: false,
      isFirstLoginEmptyEmail: false,
      passwordPolicies: {}
    }
  },
  mixins: [loginCustomParams],
  computed: {
    ...mapGetters({
      getLoginCustomParams: 'auth/getLoginCustomParams',
      getUser: 'getUser'
    }),
    bgButtonColor() {
      return invalid =>
        invalid
          ? { backgroundColor: '#E2E2E2' }
          : this.boxLogin.styles.form.buttons.bgColor
    },
    isActiveModalFirstLogin() {
      return store.activeModalFirstLogin
    }
  },
  methods: {
    ...mapActions({ setStateNotOnBoarding: 'setStateNotOnboarding' }),
    async submitNextStep() {
      this.isLoadingFirstLogin = true
      this.disabledButton = true
      try {
        await this.user.firstLoginResetPass(
          this.firstLoginFields.password,
          this.firstLoginFields.email
        )
        this.isLoadingFirstLogin = false
        this.disabledButton = false
        if (isEmpty(this.firstLoginFields.email)) {
          this.isFirstLoginEmptyEmail = true
        } else {
          this.isFirstLoginSuccess = true
        }
      } catch (error) {
        this.handleErrors(error)
      }
    },
    changeEmailSuccess() {
      this.isFirstLoginSuccess = true
      this.isFirstLoginEmptyEmail = false
    },
    handleErrors(error) {
      try {
        this.isLoadingFirstLogin = false
        this.disabledButton = false
        this.firstLoginErrors.showMessage = true
        typeof error === 'string'
          ? (this.firstLoginErrors.message = this.$t(
              `message.errorReference.${error}`
            ))
          : (this.firstLoginErrors.message = this.$t(
              `message.errorReference.${error.response.data.code}`
            ))
      } catch (e) {
        this.isLoadingFirstLogin = false
        this.disabledButton = false
        this.$store.dispatch('clearState')
      }
    },
    async getPasswordPolicies() {
      await this.company
        .passwordPolicies()
        .then(response => {
          this.passwordPolicies= response.data
        })
        .catch(error => {})
    },
    activeModalFirstLogin() {
      mutation.setActiveModalFirstLogin(true)
    }
  },
  mounted() {
    this.firstLoginFields.email = this.getUser.hasOwnProperty('mail')
      ? this.getUser.mail
      : ''
    this.getPasswordPolicies()
  },
  created() {
    this.objectLoginParams = this.getLoginCustomParams.loginCustom
    this.user = new Users()
    this.company = new Company()
  }
}
</script>

<style scoped lang="scss">
.format-img {
  height: 100%;
  max-width: 100%;
  vertical-align: middle;
  width: 100%;
  -ms-interpolation-mode: bicubic;
  display: inline-block;
}
::v-deep .zt-button {
  height: 3.25rem;
}
</style>
